/**
 * Domain model and pure functions for HIS V4 Document screen (แบบฟอร์ม · เอกสารสแกน · ใบรับรองแพทย์)
 */

import { diagnosisFromApi } from "./examModelV4";
import type { MockScannedDoc } from "./mockDocumentV4";

/** เอกสารสแกน 1 ชุด: BE เก็บ 1 แถวต่อ 1 หน้า จึงต้องจำ id ทุกแถวไว้ใช้ตอนยกเลิก */
export type ScannedDocItem = MockScannedDoc & {
  rowIds: number[];
  typeId?: number;
  categoryId?: number;
  encounterId?: number;
  documentDate: string;
  expiryDate: string;
  ownerDivisionId?: number;
  scanDivisionId?: number;
  doctorId?: number;
  doctorName: string;
};

/** ใบรับรอง 1 ใบจาก action log เนื้อหาเต็มอ่านเพิ่มเมื่อเปิดดู (certDetail) */
export type MedicalCertItem = {
  id: number;
  /** เลขที่ใช้ id ของ FormData ตาม V3 */
  no: string;
  type: string;
  formCode: string;
  day: string;
  doctor: string;
  printCount?: number;
};

/** ชนิดใบรับรองจาก GET /apis/DPO/doctor-certificates-type/ */
export type MedCertTypeItem = { code: string; name: string; formId?: number };

export type MedCertDetail = {
  id: number;
  formCode: string;
  formName: string;
  encounterId?: number;
  doctorId?: number;
  data: Record<string, any>;
};

/** HIS V4 ออกและพิมพ์ได้เฉพาะกลุ่มนี้ ชนิดอื่นดูประวัติได้แต่ออกจากเมนู V3 */
const EXAM_ABSENCE_CODE_PREFIX = "CardExamAbsenceCertificate";

export const isPrintableCertCode = (code: string): boolean =>
  code.startsWith(EXAM_ABSENCE_CODE_PREFIX);

/** หน้าเอกสารที่รออัปโหลด image เป็น JPEG base64 แบบไม่มี data: prefix */
export type ScanPage = {
  name: string;
  image: string;
  /** เลขหน้าเดิมเมื่อส่งซ้ำเฉพาะหน้าที่ล้มเหลว */
  pageNo?: number;
};

export type DocumentTypeItem = { id: number; name: string; categoryId?: number };
export type DocumentCategoryItem = { id: number; name: string; parentId?: number };

/** DATA_UPLOAD_MAX_MEMORY_SIZE ของ BE คือ 11,242,880 ไบต์ต่อคำขอ เผื่อ field อื่นใน body */
export const MAX_SCAN_IMAGE_BASE64_LENGTH = 11_000_000;

/** document_no และ document_code ของ ScannedDocument ยาวได้ 50 ตัวอักษร */
const SCANNED_DOC_TEXT_MAX_LENGTH = 50;

/**
 * prefix ของ document_code ที่ V4 ใช้รวมหลายหน้าเป็น 1 ชุด
 * ต้องมี prefix เพราะระบบเดิมใช้ field นี้เก็บรหัสแบบฟอร์มที่ซ้ำกันได้หลายเอกสาร เช่น FM-CNICU-027
 */
export const DOCUMENT_CODE_PREFIX = "V4-";

export const newDocumentCode = (now: number = Date.now(), random: number = Math.random()): string =>
  `${DOCUMENT_CODE_PREFIX}${now.toString(36)}-${random.toString(36).slice(2, 12)}`;

export interface ClinicFormItem {
  id: number;
  name: string;
  code?: string;
  printApi?: string;
  jasperPath?: string;
  jasperModule?: string;
  /** แถวเดิมจาก BE: แบบฟอร์มของ V3 อ่าน header จากแถวนี้ */
  source?: Record<string, any>;
}

export const asNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
};

export type DocumentIds = {
  encounterId?: number;
  patientId?: number;
  patientHn?: string;
  doctorId?: number;
  emrId?: number;
};

export type DocumentRunParams = {
  sequence: string;
  action: string;
  card?: string;
  restart?: boolean;
  encounterId?: number;
  patientId?: number;
  patientHn?: string;
  doctorId?: number;
  emrId?: number;
  /** ผูกผลของ action กับคำขอที่ card รออยู่ */
  requestId?: string;
  [key: string]: unknown;
};

export type DocumentRunSequenceProp = (params: DocumentRunParams) => void;

/** ready = มี Principal, none = ยังไม่ได้ลงวินิจฉัย, failed = อ่านจาก EMR ไม่สำเร็จ */
export type DiagnosisStatus = "idle" | "ready" | "none" | "failed";

/** ผลของ action เขียนข้อมูล: card แสดงว่าสำเร็จหรือล้างฟอร์มหลังได้ค่านี้เท่านั้น */
export type DocumentActionResult = {
  requestId: string;
  action: string;
  ok: boolean;
  label?: string;
  /** บันทึกสำเร็จแล้ว แต่โหลดรายการใหม่ไม่สำเร็จ */
  reloadFailed?: boolean;
  /** ใช้ส่งซ้ำเข้าชุดเดิมเมื่อบางหน้าล้มเหลว */
  documentCode?: string;
  failedPages?: number[];
};

/**
 * Slice ของ state ใน sequence DocumentV4
 * กฎสถาปัตยกรรม: ห้ามมี field loading / saving / error ใน slice
 */
export type DocumentV4Slice = {
  forms: ClinicFormItem[];
  documentTypes: DocumentTypeItem[];
  documentCategories: DocumentCategoryItem[];
  scannedDocs: ScannedDocItem[];
  medicalCerts: MedicalCertItem[];
  medCertTypes: MedCertTypeItem[];
  certDetail: MedCertDetail | null;
  patientHn?: string;
  encounterId?: number;
  patientId?: number;
  doctorId?: number;
  emrId?: number;
  /** selectedEncounter ไม่มี field นี้ จึงอ่านจาก EncounterDetail */
  encounterType?: string;
  diagnosisText: string;
  diagnosisStatus: DiagnosisStatus;
  actionResult: DocumentActionResult | null;
  sequenceIndex?: string;
};

export const emptyDocumentV4Slice = (): DocumentV4Slice => ({
  forms: [],
  documentTypes: [],
  documentCategories: [],
  scannedDocs: [],
  medicalCerts: [],
  medCertTypes: [],
  certDetail: null,
  patientHn: "",
  encounterId: undefined,
  patientId: undefined,
  doctorId: undefined,
  emrId: undefined,
  encounterType: undefined,
  diagnosisText: "",
  diagnosisStatus: "idle",
  actionResult: null,
  sequenceIndex: "START",
});

export const normalizeDocumentV4Slice = (
  raw?: Partial<DocumentV4Slice> | null,
): DocumentV4Slice => {
  if (!raw) return emptyDocumentV4Slice();
  return {
    forms: Array.isArray(raw.forms) ? raw.forms : [],
    documentTypes: Array.isArray(raw.documentTypes) ? raw.documentTypes : [],
    documentCategories: Array.isArray(raw.documentCategories) ? raw.documentCategories : [],
    scannedDocs: Array.isArray(raw.scannedDocs) ? raw.scannedDocs : [],
    medicalCerts: Array.isArray(raw.medicalCerts) ? raw.medicalCerts : [],
    medCertTypes: Array.isArray(raw.medCertTypes) ? raw.medCertTypes : [],
    certDetail: raw.certDetail ?? null,
    patientHn: raw.patientHn ?? "",
    encounterId: raw.encounterId,
    patientId: raw.patientId,
    doctorId: raw.doctorId,
    emrId: raw.emrId,
    encounterType: raw.encounterType,
    diagnosisText: raw.diagnosisText ?? "",
    diagnosisStatus: raw.diagnosisStatus ?? "idle",
    actionResult: raw.actionResult ?? null,
    sequenceIndex: raw.sequenceIndex ?? "Action",
  };
};

/**
 * แปลงวันที่สตริงแบบ วว/ดด/ปปปป (พ.ศ.) ให้เป็นตัวเลข YYYYMMDD เพื่อใช้เปรียบเทียบช่วงเวลา
 * เช่น "26/10/2569" -> 25691026
 * ถ้าไม่ตรงรูปแบบ คืนค่า null
 */
export const parseThaiDateNumber = (dateStr?: string | null): number | null => {
  if (!dateStr || typeof dateStr !== "string") return null;
  const match = /(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(dateStr.trim());
  if (!match) return null;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  if (day < 1 || day > 31 || month < 1 || month > 12 || year < 2400) return null;
  return year * 10000 + month * 100 + day;
};

/**
 * ตรวจสอบความถูกต้องของรูปแบบวันที่ วว/ดด/ปปปป (พ.ศ.)
 */
export const isValidThaiDate = (dateStr: string): boolean => {
  if (!dateStr || !dateStr.trim()) return true; // ว่างถือว่าไม่ติดเงื่อนไข
  return parseThaiDateNumber(dateStr) !== null;
};

/** วันที่ปัจจุบันแบบ วว/ดด/ปปปป (พ.ศ.) */
export const todayThaiDate = (now: Date = new Date()): string => {
  const day = String(now.getDate()).padStart(2, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${now.getFullYear() + 543}`;
};

/** ข้อความวินิจฉัยบนใบรับรอง: "{ชื่อโรค} ({รหัส})" จาก Principal (#86627) */
export const diagnosisTextFromApi = (payload: unknown): string => {
  const principal = diagnosisFromApi(payload).principal;
  if (principal === null) return "";
  const name = principal.medicalDescription || principal.icdTerm;
  if (name && principal.icdCode) return `${name} (${principal.icdCode})`;
  return name || principal.icdCode;
};

/** เหตุที่ยังออกใบรับรองไม่ได้ คืน null เมื่อข้อมูลครบ */
export const medCertIssueProblem = (input: {
  canIssue: boolean;
  encounterId?: number;
  encounterType?: string;
  patientName: string;
  doctorId?: number;
  typeCode: string;
  dx: string;
  rest: boolean;
  fromDate: string;
  days: string;
}): string | null => {
  if (!input.canIssue) return "ออกใบรับรองแพทย์ได้เฉพาะแพทย์";
  if (!input.encounterId) return "ยังไม่มีข้อมูล Encounter";
  if (!input.encounterType) return "ยังไม่ทราบประเภท Encounter";
  if (input.encounterType !== "OPD") return "ใบรับรองของผู้ป่วยในยังต้องออกจากเมนู V3";
  if (!input.patientName) return "ยังไม่มีข้อมูลผู้ป่วย";
  if (!input.doctorId) return "ยังไม่มีแพทย์ของ Encounter นี้";
  if (!input.typeCode) return "ยังไม่มีชนิดใบรับรองที่ออกจาก HIS V4 ได้ กรุณาตรวจการตั้งค่า";
  if (!input.dx) return "ยังไม่ได้ลงวินิจฉัย กรุณาลง Principal Diagnosis ที่หน้าตรวจรักษาก่อน";
  if (input.rest) {
    if (parseThaiDateNumber(input.fromDate) === null) {
      return "กรุณาระบุวันที่เริ่มพักในรูปแบบ วว/ดด/ปปปป (พ.ศ.)";
    }
    if (!/^[1-9]\d*$/.test(input.days.trim())) return "กรุณาระบุจำนวนวันพักเป็นตัวเลข";
  }
  return null;
};

const THAI_MONTHS = [
  "มกราคม",
  "กุมภาพันธ์",
  "มีนาคม",
  "เมษายน",
  "พฤษภาคม",
  "มิถุนายน",
  "กรกฎาคม",
  "สิงหาคม",
  "กันยายน",
  "ตุลาคม",
  "พฤศจิกายน",
  "ธันวาคม",
];

const pad2 = (value: number): string => String(value).padStart(2, "0");

/** อายุ [ปี, เดือน, วัน] ณ วันที่ตรวจ คืน null เมื่อไม่มีวันเกิดที่อ่านได้ */
export const ageParts = (birthdateBE: unknown, at: Date): [number, number, number] | null => {
  const dateNum = parseThaiDateNumber(typeof birthdateBE === "string" ? birthdateBE : "");
  if (dateNum === null) return null;
  const birth = new Date(Math.floor(dateNum / 10000) - 543, (Math.floor(dateNum / 100) % 100) - 1, dateNum % 100);

  let years = at.getFullYear() - birth.getFullYear();
  let months = at.getMonth() - birth.getMonth();
  let days = at.getDate() - birth.getDate();
  if (days < 0) {
    months -= 1;
    days += new Date(at.getFullYear(), at.getMonth(), 0).getDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  return [years, months, days];
};

/** วันสุดท้ายของการพัก นับวันเริ่มเป็นวันแรก */
export const restEndDate = (fromDate: string, days: number): string => {
  const dateNum = parseThaiDateNumber(fromDate);
  if (dateNum === null) return "";
  const year = Math.floor(dateNum / 10000) - 543;
  const end = new Date(year, (Math.floor(dateNum / 100) % 100) - 1, (dateNum % 100) + days - 1);
  return todayThaiDate(end);
};

/**
 * data ของ CardExamAbsenceCertificate โครงเดียวกับ formatExamAbsenceReport ของ V3
 * เพื่อให้ V3 เปิดและพิมพ์ใบที่ออกจาก V4 ได้ และแบบฟอร์ม pdfmake เดิมอ่านได้
 */
export const examAbsenceCertData = (input: {
  formCode: string;
  patient: Record<string, any>;
  encounter: { id?: number; number?: string; type?: string };
  doctor: { name: string; nameEn: string; certNumber: string };
  chiefComplaint: string;
  diagnosis: string;
  comment: string;
  rest: boolean;
  fromDate: string;
  days: number;
  now?: Date;
}): Record<string, any> => {
  const now = input.now ?? new Date();
  const examDate = todayThaiDate(now);
  const time = `${pad2(now.getHours())}:${pad2(now.getMinutes())}`;
  const age = ageParts(input.patient.birthdate, now);
  const opdStartDate = input.rest ? input.fromDate : "";
  const opdEndDate = input.rest ? restEndDate(input.fromDate, input.days) : "";
  const yearAD = now.getFullYear();
  const monthEN = now.toLocaleString("en-US", { month: "long" });

  return {
    reportType: input.formCode,
    reportDuplicate: "ต้นฉบับ",
    reportLanguage: "ไทย",
    printCount: 1,
    createdDate: `${examDate}, ${time}`,
    createDateForm: `${pad2(now.getDate())}/${pad2(now.getMonth() + 1)}/${yearAD}, ${time}`,
    formatCreatedTime: time,
    formatCreatedDateTH: `${now.getDate()} ${THAI_MONTHS[now.getMonth()]} ${yearAD + 543}`,
    formatCreatedDate: `${monthEN} ${now.getDate()}, ${yearAD}`,
    patientInfo: {
      full_name: input.patient.full_name ?? "",
      hn: input.patient.hn ?? "",
      citizen_no: input.patient.citizen_no ?? "",
      citizen_passport: input.patient.citizen_passport ?? "",
      birthdate: input.patient.birthdate ?? "",
    },
    encounterInfo: {
      id: input.encounter.id,
      number: input.encounter.number ?? "",
      type: input.encounter.type ?? "",
    },
    ageYear: age?.[0] ?? "-",
    ageMonth: age?.[1] ?? "-",
    ageDay: age?.[2] ?? "-",
    doctorName: input.doctor.name,
    doctorNameEn: input.doctor.nameEn,
    doctorCertNumber: input.doctor.certNumber,
    chiefComplaint: input.chiefComplaint,
    diagnosis: input.diagnosis,
    comment: input.comment,
    examDate,
    shouldOpdAbsence: input.rest,
    shouldIpdAbsence: false,
    opdStartDate,
    opdEndDate,
    dayDuration: input.rest ? String(input.days) : "",
    patientVisitType: ["ผู้ป่วยนอก", "Outpatient"],
    patientExamDate: [examDate, examDate],
    patientAbsenceStart: opdStartDate,
    patientAbsenceEnd: opdEndDate,
  };
};

/**
 * กรองรายชื่อแบบฟอร์มคลินิกตามข้อความค้นหา
 */
export const filterClinicForms = (
  forms: (string | ClinicFormItem)[] = [],
  query: string = "",
): ClinicFormItem[] => {
  const q = query.trim().toLowerCase();
  return forms
    .map((item, id) => {
      if (typeof item === "string") {
        return { id, name: item };
      }
      return item;
    })
    .filter((f) => !q || f.name.toLowerCase().includes(q));
};

export type ScannedDocFilter = {
  typeId?: number;
  /** หมวดที่เลือกรวมหมวดย่อยทั้งหมด (จาก categoryIdsWithin) */
  categoryIds?: number[];
  query?: string;
  scanFrom?: string;
  scanTo?: string;
  docFrom?: string;
  docTo?: string;
  expiryFrom?: string;
  expiryTo?: string;
  ownerDivisionId?: number;
  scanDivisionId?: number;
  doctorId?: number;
  encounterId?: number;
};

/** หมวดที่เลือกและหมวดย่อยทุกชั้น เหมือนการกรอง document_category ของ BE */
export const categoryIdsWithin = (
  categories: DocumentCategoryItem[],
  categoryId: number,
): number[] => {
  const ids = [categoryId];
  for (let index = 0; index < ids.length; index += 1) {
    categories.forEach((category) => {
      if (category.parentId === ids[index] && !ids.includes(category.id)) ids.push(category.id);
    });
  }
  return ids;
};

/** วันที่ที่พิมพ์ผิดไม่ถูกใช้กรอง ส่วนเอกสารที่ไม่มีวันที่นั้นไม่ผ่านช่วงที่ระบุ */
const inDateRange = (value: string, from?: string, to?: string): boolean => {
  const fromNum = parseThaiDateNumber(from);
  const toNum = parseThaiDateNumber(to);
  if (fromNum === null && toNum === null) return true;
  const valueNum = parseThaiDateNumber(value);
  if (valueNum === null) return false;
  return (fromNum === null || valueNum >= fromNum) && (toNum === null || valueNum <= toNum);
};

/** กรองเอกสารสแกน รายการทั้งหมดของผู้ป่วยโหลดมาแล้วจึงกรองฝั่ง client ได้ครบ */
export const filterScannedDocs = (
  docs: ScannedDocItem[],
  filter: ScannedDocFilter = {},
): ScannedDocItem[] => {
  const q = (filter.query ?? "").trim().toLowerCase();

  return docs.filter((doc) => {
    if (filter.typeId !== undefined && doc.typeId !== filter.typeId) return false;
    if (
      filter.categoryIds !== undefined &&
      (doc.categoryId === undefined || !filter.categoryIds.includes(doc.categoryId))
    ) {
      return false;
    }
    if (q && !doc.name.toLowerCase().includes(q)) return false;
    if (filter.ownerDivisionId !== undefined && doc.ownerDivisionId !== filter.ownerDivisionId) {
      return false;
    }
    if (filter.scanDivisionId !== undefined && doc.scanDivisionId !== filter.scanDivisionId) {
      return false;
    }
    if (filter.doctorId !== undefined && doc.doctorId !== filter.doctorId) return false;
    if (filter.encounterId !== undefined && doc.encounterId !== filter.encounterId) return false;
    return (
      inDateRange(doc.at, filter.scanFrom, filter.scanTo) &&
      inDateRange(doc.documentDate, filter.docFrom, filter.docTo) &&
      inDateRange(doc.expiryDate, filter.expiryFrom, filter.expiryTo)
    );
  });
};

/**
 * สร้างชื่อเอกสารสแกนใหม่เมื่อกดบันทึก
 */
export const formatNextDocName = (
  type: string,
  note: string,
  hn: string,
  seq: number,
): string => {
  const prefix = note.trim() || type.split(" ")[0];
  const seqStr = String(seq).padStart(4, "0");
  const suffix = [hn, seqStr].filter(Boolean).join("_");
  const prefixMax = SCANNED_DOC_TEXT_MAX_LENGTH - suffix.length - 1;
  return [prefix.slice(0, Math.max(prefixMax, 0)), suffix].filter(Boolean).join("_");
};

/**
 * Mappers จาก API ของ V3
 */

/** แปลงรายการแบบฟอร์มจาก GET /apis/core/document-type/?printable=true */
export const formsFromApi = (items: any[]): ClinicFormItem[] => {
  if (!Array.isArray(items)) return [];
  return items.map((item, idx) => ({
    id: item.id ?? idx + 1,
    name: item.name ?? item.code ?? `แบบฟอร์ม ${idx + 1}`,
    code: item.code,
    printApi: item.print_api,
    jasperPath: item.jasper_path,
    jasperModule: item.jasper_module,
    source: item,
  }));
};

/** แปลงประเภทเอกสารจาก GET /apis/core/document-type/ */
export const documentTypesFromApi = (items: any): DocumentTypeItem[] => {
  if (!Array.isArray(items)) return [];
  return items
    .filter((item) => asNumber(item?.id) !== undefined)
    .map((item) => ({
      id: Number(item.id),
      name: String(item.name ?? item.code ?? ""),
      categoryId: asNumber(item.category),
    }));
};

/** แปลงหมวดเอกสารจาก GET /apis/core/document-category/ */
export const documentCategoriesFromApi = (items: any): DocumentCategoryItem[] => {
  if (!Array.isArray(items)) return [];
  return items
    .filter((item) => asNumber(item?.id) !== undefined)
    .map((item) => ({
      id: Number(item.id),
      name: String(item.name ?? item.code ?? ""),
      parentId: asNumber(item.parent),
    }));
};

/**
 * แปลงแถวจาก GET /apis/core/scanned-document/ เป็นเอกสาร
 * แถวที่ V4 บันทึกรวมเป็นชุดตาม document_code ส่วนแถวเดิมแสดงแถวละ 1 เอกสาร
 */
export const scannedDocsFromApi = (items: any[]): ScannedDocItem[] => {
  if (!Array.isArray(items)) return [];

  const groups = new Map<string, any[]>();
  items
    .filter((item) => item.active !== false && asNumber(item.id) !== undefined)
    .forEach((item) => {
      const code = String(item.document_code ?? "");
      const key = code.startsWith(DOCUMENT_CODE_PREFIX) ? code : `row-${item.id}`;
      groups.set(key, [...(groups.get(key) ?? []), item]);
    });

  return Array.from(groups.values())
    .map((rows) => {
      const sorted = [...rows].sort(
        (a, b) => (asNumber(a.page) ?? 0) - (asNumber(b.page) ?? 0) || Number(a.id) - Number(b.id),
      );
      const first = sorted[0];
      const rowIds = sorted.map((row) => Number(row.id));
      return {
        id: Math.min(...rowIds),
        rowIds,
        name: first.document_no || first.document_type_name || `เอกสาร ${first.id}`,
        type: first.document_type_name || first.document_category_name || "",
        typeId: asNumber(first.document_type),
        categoryId: asNumber(first.document_category_id),
        encounterId: asNumber(first.encounter),
        at: first.scan_date || first.created || "",
        documentDate: first.document_date || "",
        expiryDate: first.expiry_date || "",
        ownerDivisionId: asNumber(first.owner_division),
        scanDivisionId: asNumber(first.scan_division),
        doctorId: asNumber(first.doctor_id),
        doctorName: first.doctor_fullname || "",
        by: first.scan_user_name || "",
        files: sorted.map((row) => row.document_image).filter(Boolean),
      };
    })
    .sort((a, b) => b.id - a.id);
};

/** แปลงชนิดใบรับรองจาก master ตัดชนิดที่ไม่ได้ผูก form และ MedicalCertificateCUDent แบบ V3 */
export const medCertTypesFromApi = (items: any): MedCertTypeItem[] => {
  if (!Array.isArray(items)) return [];
  return items
    .filter(
      (item) => item?.form_type_code && item.form_type_code !== "MedicalCertificateCUDent",
    )
    .map((item) => ({
      code: String(item.form_type_code),
      name: String(item.name ?? item.form_type_code),
      formId: asNumber(item.form),
    }));
};

/**
 * ประวัติใบรับรองจาก GET /apis/PTM/form-data-action-log-all/ ตาม V3
 * log มีแถวละ action จึงใช้แถวล่าสุดของแต่ละใบ และตัดใบที่ถูกยกเลิก
 */
export const medCertsFromLogs = (items: any, codes: string[]): MedicalCertItem[] => {
  if (!Array.isArray(items)) return [];

  const latest = new Map<number, any>();
  items
    .filter((item) => codes.includes(item?.form_type_code) && asNumber(item?.statable) !== undefined)
    .forEach((item) => {
      const id = Number(item.statable);
      const current = latest.get(id);
      if (current === undefined || Number(item.id) > Number(current.id)) latest.set(id, item);
    });

  return Array.from(latest.entries())
    .filter(([, item]) => item.action !== "CANCEL")
    .map(([id, item]) => {
      const tracked = item.tracked_data ?? {};
      return {
        id,
        no: String(id),
        type: item.form_type_name || item.form_type_code,
        formCode: item.form_type_code,
        day: item.edited || "",
        doctor: tracked.doctorName || item.name || "",
        printCount: asNumber(tracked.printCount ?? tracked.print_count),
      };
    })
    .sort((a, b) => b.id - a.id);
};

/** ชื่อและเลขใบอนุญาตแพทย์จาก DoctorDetail แบบ GetCommonInfoForMedCert ของ V3 */
export const doctorForCertFromApi = (
  doctor: any,
): { name: string; nameEn: string; certNumber: string } => ({
  name: String(doctor?.name_code || doctor?.full_name || "").replace(/\s*\(.*?\)$/, ""),
  nameEn: [doctor?.first_name_en, doctor?.last_name_en].filter(Boolean).join(" "),
  certNumber: String(doctor?.certificate_no ?? ""),
});

/** เป็นสำเนาเมื่อพิมพ์มากกว่า 1 ครั้ง ตาม V3 */
export const isCertCopy = (data: Record<string, any> | null | undefined): boolean =>
  Number(data?.printCount ?? data?.print_count ?? 0) > 1;

/** ประกอบ payload ของ 1 หน้า POST /apis/core/scanned-document/ (BE เก็บแถวละ 1 ภาพ) */
export const scannedDocPagePayload = (params: {
  patientId: number;
  encounterId?: number;
  docName: string;
  typeId: number;
  documentCode: string;
  ownerDivisionId?: number;
  image: string;
  page: number;
}): Record<string, any> => ({
  patient: params.patientId,
  encounter: params.encounterId ?? null,
  document_no: params.docName,
  document_code: params.documentCode,
  document_type: params.typeId,
  document_image: params.image,
  page: params.page,
  ...(params.ownerDivisionId === undefined ? {} : { owner_division: params.ownerDivisionId }),
});

/**
 * ประกอบ payload POST /apis/PTM/form-data/
 * ส่ง form id จาก master เป็นหลัก เพราะการหาด้วยรหัสและชื่อจะได้ form ว่างเมื่อชื่อไม่ตรง DB
 */
export const medCertCreatePayload = (params: {
  encounterId: number;
  doctorId: number;
  type: MedCertTypeItem;
  data: Record<string, any>;
}): Record<string, any> => ({
  action: "SAVE",
  encounter: params.encounterId,
  doctor: params.doctorId,
  ...(params.type.formId === undefined
    ? { form_code: params.type.code, form_name: params.type.name, form_version: "1.0" }
    : { form: params.type.formId }),
  data: params.data,
});

/** ประกอบ payload พิมพ์ซ้ำ PUT /apis/PTM/form-data/{id}/ นับจำนวนพิมพ์เพิ่มแบบ V3 */
export const medCertReprintPayload = (detail: MedCertDetail): Record<string, any> => {
  const printCount = Number(detail.data.printCount ?? detail.data.print_count ?? 0);
  return {
    action: "SAVE",
    encounter: detail.encounterId,
    ...(detail.doctorId === undefined ? {} : { doctor: detail.doctorId }),
    form_code: detail.formCode,
    form_name: detail.formName,
    form_version: "1.0",
    data: { ...detail.data, printCount: printCount + 1 },
  };
};
